import React, { useState, useRef } from 'react';
import { 
  Download, 
  Share2, 
  Printer, 
  X, 
  Copy,
  CheckCircle,
  User,
  Calendar,
  Hash,
  Percent,
  DollarSign
} from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { useTranslation } from 'react-i18next';

const StockTransferReceipt = ({ transaction, onClose, isOpen }) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const receiptRef = useRef(null);

  if (!isOpen || !transaction) return null;

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }),
      time: date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
    };
  };

  const { date, time } = formatDate(transaction.createdAt);

  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: t('stockTransferReceipt.shareTitle'),
          text: t('stockTransferReceipt.shareText', {
            amount: transaction.amount,
            agent: transaction.downlineAgent?.username || '-'
          }),
          url: window.location.href,
        });
      } else {
        copyToClipboard();
      }
    } catch (error) {
      console.log('Sharing failed', error);
    }
  };

  const copyToClipboard = () => {
    const receiptText = `
      ${t('stockTransferReceipt.title')}
      ${t('stockTransferReceipt.transactionId')}: ${transaction.id || '-'}
      ${t('stockTransferReceipt.date')}: ${date}
      ${t('stockTransferReceipt.time')}: ${time}
      ${t('stockTransferReceipt.agent')}: ${transaction.downlineAgent?.username || '-'}
      ${t('stockTransferReceipt.amount')}: ${transaction.amount} ${t('common3.currency')}
      ${t('stockTransferReceipt.commissionRate')}: ${transaction.commission_rate}%
      ${t('stockTransferReceipt.totalAmount')}: ${transaction.total_amount} ${t('common3.currency')}
    `;
    
    navigator.clipboard.writeText(receiptText)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(err => {
        console.error('Failed to copy: ', err);
      });
  };

  const downloadPDF = () => {
    const input = receiptRef.current;
    
    html2canvas(input, { scale: 2 })
      .then((canvas) => {
        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF('p', 'mm', 'a5');
        const imgWidth = 140;
        const pageHeight = pdf.internal.pageSize.height;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        let heightLeft = imgHeight;
        let position = 0;

        pdf.addImage(imgData, 'PNG', 10, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;

        while (heightLeft >= 0) {
          position = heightLeft - imgHeight;
          pdf.addPage();
          pdf.addImage(imgData, 'PNG', 10, position, imgWidth, imgHeight);
          heightLeft -= pageHeight;
        }

        pdf.save(`Stock_Transfer_Receipt_${transaction.id || Date.now()}.pdf`);
      });
  };

  const printReceipt = () => {
    const printContent = receiptRef.current.innerHTML;
    const originalContent = document.body.innerHTML;
    
    document.body.innerHTML = printContent;
    window.print();
    document.body.innerHTML = originalContent;
    window.location.reload();
  };

  return (
    <div className="fixed inset-0 bg-black/30 bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
        <div className="flex justify-between items-center p-4 border-b">
          <h2 className="text-xl font-semibold text-gray-800">{t('stockTransferReceipt.title')}</h2>
          <button 
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X size={24} />
          </button>
        </div>
        
    
        <div ref={receiptRef} className="p-6 bg-white">

          <div className="text-center mb-6">
            <div className="flex justify-center mb-2">
              <div className=" h-9 pb-[12px]  rounded-full flex items-center justify-center">
                 <img
                className="dark:hidden w-auto h-[40px]"
                src="/images/afh-logo.png"
                alt="Logo"
                width={150}
                height={40}
              />
              </div>
            </div>
            <h1 className="text-2xl font-bold text-gray-800">YES Charge Merchant Portal</h1>
            <p className="text-gray-600">{t('stockTransferReceipt.subtitle')}</p>
          </div>
          
   
          <div className="space-y-4 mb-6">
            <div className="flex justify-between items-center">
              <div className="flex items-center text-gray-600">
                <Hash size={16} className="mr-2" />
                <span>{t('stockTransferReceipt.transactionId')}</span>
              </div>
              <span className="font-mono text-gray-800">{transaction.id || '-'}</span>
            </div>
            
            <div className="flex justify-between items-center">
              <div className="flex items-center text-gray-600">
                <Calendar size={16} className="mr-2" />
                <span>{t('stockTransferReceipt.dateTime')}</span>
              </div>
              <div className="text-right">
                <div className="text-gray-800">{date}</div>
                <div className="text-sm text-gray-600">{time}</div>
              </div>
            </div>
            
            <div className="flex justify-between items-center">
              <div className="flex items-center text-gray-600">
                <User size={16} className="mr-2" />
                <span>{t('stockTransferReceipt.agent')}</span>
              </div>
              <span className="font-medium text-gray-800">{transaction.downlineAgent?.username || '-'}</span>
            </div>
            
            <div className="flex justify-between items-center">
              <div className="flex items-center text-gray-600">
                <DollarSign size={16} className="mr-2" />
                <span>{t('stockTransferReceipt.amount')}</span>
              </div>
              <span className="font-medium text-gray-800">{transaction.amount} {t('common3.currency')}</span>
            </div>
            
            <div className="flex justify-between items-center">
              <div className="flex items-center text-gray-600">
                <Percent size={16} className="mr-2" />
                <span>{t('stockTransferReceipt.commissionRate')}</span>
              </div>
              <span className="font-medium text-gray-800">{transaction.commission_rate}%</span>
            </div>
            
            <div className="border-t pt-4 mt-4">
              <div className="flex justify-between items-center text-lg font-bold">
                <span className="text-gray-800">{t('stockTransferReceipt.totalAmount')}</span>
                <span className="text-blue-600">{transaction.total_amount} {t('common3.currency')}</span>
              </div>
            </div>
          </div>
          
          {/* Footer Message */}
          <div className="text-center text-sm text-gray-500 border-t pt-4">
            <p>{t('stockTransferReceipt.thankYou')}</p>
            <p>{t('stockTransferReceipt.support')}: support@afrh.com</p>
          </div>
        </div>
        
        {/* Action Buttons */}
        <div className="flex flex-wrap justify-between p-4 border-t gap-2">
          <button
            onClick={copyToClipboard}
            className="flex items-center px-3 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
          >
            {copied ? <CheckCircle size={18} className="mr-1 text-green-600" /> : <Copy size={18} className="mr-1" />}
            {copied ? t('common4.copied') : t('common4.copy')}
          </button>
          
          <button
            onClick={handleShare}
            className="flex items-center px-3 py-2 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
          >
            <Share2 size={18} className="mr-1" />
            {t('common4.share')}
          </button>
          
          <button
            onClick={downloadPDF}
            className="flex items-center px-3 py-2 bg-purple-100 text-purple-700 rounded hover:bg-purple-200 transition-colors"
          >
            <Download size={18} className="mr-1" />
            PDF
          </button>
          
          <button
            onClick={printReceipt}
            className="flex items-center px-3 py-2 bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors"
          >
            <Printer size={18} className="mr-1" />
            {t('common4.print')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default StockTransferReceipt;